/**
 * The small pieces more than one surface draws, and the one rule they carry.
 *
 * A kind and a state must not be able to look alike. Before that rule, IDEA in
 * accent tint was the same chip as BEING FIXED and SEARCH MISS was the same as
 * NEXT UP, which taught a reader that the colour of a kind chip means
 * something. So: a kind is a classification that never changes and carries no
 * colour and a square corner; a state changes over time and keeps the colour
 * and takes the pill. Shape as well as colour, because colour alone is not a
 * distinction every reader can make (WCAG 1.4.1).
 *
 * That shape is mechanism and lives here in code. The word and the tone come
 * out of submission_type and status_label, so a development team changes what
 * people read with one row (FR-5a) and cannot change a state chip into
 * something that looks like a kind.
 */
import { Badge } from "@depicomnlbv/platform-ui";

import type { Chip } from "./types";

/** Put values into a template from copy.ts. Missing keys are left visible on purpose. */
export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in values ? String(values[key]) : whole,
  );
}

/**
 * A kind chip. The tone the row carries is deliberately not painted: under the
 * rule above a kind has no state colour at all, so submission_type.chip_tone
 * has nothing to do on this surface. It is read and dropped here rather than
 * quietly ignored somewhere further down, and it is named in the task report
 * as a column the panel cannot honour.
 */
export function KindChip({ chip }: { chip: Chip }) {
  return <Badge className="pf-chip-kind">{chip.label}</Badge>;
}

/** A state chip: the word and the tone from status_label, the pill from here. */
export function StateChip({ chip }: { chip: Chip }) {
  return (
    <Badge tone={chip.tone} className="pf-chip-state">
      {chip.label}
    </Badge>
  );
}

/**
 * The four rows that travel with every submission whether or not anybody looks
 * at them. This is the whole difference between a usable report and "the app
 * is doing something odd", and it is why the panel lives in the application
 * rather than in a tab that cannot know either value.
 */
export function ContextCard({
  heading,
  rows,
}: {
  heading: string;
  rows: { key: string; value: string; code?: boolean }[];
}) {
  return (
    <div className="pf-context">
      <p className="pf-section-label">{heading}</p>
      <dl className="pf-context__grid">
        {rows.map((row) => (
          <div className="pf-context__row" key={row.key}>
            <dt className="pf-context__key">{row.key}</dt>
            <dd className={`pf-context__value${row.code ? " pf-context__value--code" : ""}`}>
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * The icons, drawn from the artboards and marked decorative.
 *
 * Every one of them sits beside a word that already says what it is, so a name
 * on the icon would be read twice by a screen reader. Where an icon is the
 * only content of a control, the control carries the name and the icon stays
 * hidden.
 */
export function Icon({ name }: { name: "bubble" | "close" | "back" | "search" | "send" | "tick" | "warn" }) {
  const shared = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    focusable: false,
  };
  switch (name) {
    case "bubble":
      return (
        <svg {...shared}>
          <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.9-.9L3 20.5l1.5-4.6A8.4 8.4 0 0 1 3.6 11.5a8.4 8.4 0 0 1 8.4-8.4h.5a8.4 8.4 0 0 1 8.5 8.4z" />
        </svg>
      );
    case "close":
      return (
        <svg {...shared}>
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      );
    case "back":
      return (
        <svg {...shared}>
          <path d="M15 18l-6-6 6-6" />
        </svg>
      );
    case "search":
      return (
        <svg {...shared}>
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-4.2-4.2" />
        </svg>
      );
    case "send":
      return (
        <svg {...shared}>
          <path d="M4 12h15M13 6l6 6-6 6" />
        </svg>
      );
    case "tick":
      return (
        <svg {...shared}>
          <path d="M5 13l4 4L19 7" />
        </svg>
      );
    case "warn":
      return (
        <svg {...shared}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4.5M12 16h.01" />
        </svg>
      );
  }
}

/**
 * FR-30. One wording for the count on every surface, and the count is always
 * the total. Two of the four forms the canvas carried counted differently:
 * "3 others hit this" is four people and "Asked for by 6 of you" is six, so a
 * reader comparing two rows could not tell whether he was in the number.
 *
 * Three forms, because English has two of them and the reader is the third.
 * Until 9 September 2026 there was one, and a count of one read "Sent in by 1
 * people". That is not an edge case: the schema says sent_in_by is the total
 * including this submission, so an item the reader sent in that nobody else has
 * sent in is a count of one with includesYou true, which is the likeliest row
 * on the From you list. Nothing caught it because no artboard drew a count of
 * one, so the canvas never asked the question.
 *
 * Which of the three to use is mechanism and lives here in code, the same way
 * the chip shapes above do. copy.ts carries the words and nothing else, so a
 * development team changes what a reader reads without touching a plural rule,
 * and cannot make a count of one say people.
 */
export function sentInBy(
  templates: { total: string; singular: string; you: string; includingYou: string },
  count: number,
  includesYou: boolean,
): string {
  if (count === 1) {
    // No includingYou on this branch: templates.you already says the reader is
    // the one, and templates.singular is the case where he is not in the count
    // at all.
    return includesYou ? templates.you : fill(templates.singular, { count });
  }
  return fill(templates.total, { count }) + (includesYou ? templates.includingYou : "");
}

/**
 * The two form case: every count whose sentence makes no claim about the
 * reader. Pick the noun English needs, and fill the rest of the sentence.
 *
 * Every two form sentence in the module comes through here, so that half of the
 * rule has one implementation: the gap list's two screen halves, the queue's
 * gap row, its waiting row, the answers to read on a card, and the queue's fact
 * row through sentInByValue. The three form rule lives once as well, in
 * sentInBy above, which searchedFor borrows. So the module has two branch
 * points on a count, one per shape, and both are in this file: the `if` in
 * sentInBy and the ternary below. `grep -rn "count === 1" widget/src` says
 * whether that is still true, and answers with those two plus this sentence.
 *
 * The other values go through because these sentences carry more than a count:
 * a gap names the screen it was searched from, and a queue row names the words.
 */
export function countedSentence(
  templates: { total: string; singular: string },
  count: number,
  values: Record<string, string | number> = {},
): string {
  return fill(count === 1 ? templates.singular : templates.total, { count, ...values });
}

/**
 * FR-30. The same count as the value half of the queue's fact row, which the
 * drawing splits into a label and a value while the lists write it as one
 * sentence.
 *
 * Two forms and not three: a QueueSubmission carries sentInBy and no
 * includesYou, so this row has no reader to name and the third case cannot
 * arise here.
 */
export function sentInByValue(
  templates: { total: string; singular: string },
  count: number,
): string {
  return countedSentence(templates, count);
}

/**
 * FR-28, and FR-30's rule applied to the other count this module renders.
 *
 * `also_searched` is the people who searched these words and found nothing, and
 * the write puts this reader among them: the first search for words nobody has
 * looked for before answers one, and that one is him. Measured over the wire on
 * 9 September 2026, and it is why this sentence said "too" about a number the
 * reader was inside. So it needs the three forms the lists needed, for the same
 * reason, and the flag that says the reader is in the number is `missRecorded`,
 * which is true exactly when a row was written for him.
 *
 * The branching is sentInBy's, not a second copy of it, because it is the same
 * rule: a total, the reader named instead of counted at one, and the suffix
 * above one. Only the name here is different, because the call site reads a
 * search count rather than a submission and renaming that helper belongs to the
 * branch it was written on.
 */
export function searchedFor(
  templates: { total: string; singular: string; nobodyElse: string; includingYou: string },
  count: number,
  includesYou: boolean,
): string {
  return sentInBy(
    {
      total: templates.total,
      singular: templates.singular,
      you: templates.nobodyElse,
      includingYou: templates.includingYou,
    },
    count,
    includesYou,
  );
}
